import classnames from 'classnames';
import { trimStart } from 'lodash';
import React, { ChangeEvent, FocusEvent, KeyboardEvent } from 'react';
import searchLightIconBeta from './search-light.svg';
import searchDarkIconBeta from './search-dark.svg';
import { ENTER_KEY_NAME } from '@mm/constants/keys-codes';
import './SearchInput.pcss';

type Props = {
  value?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  onSearch?: (value: string) => void;
  className?: string;
  iconClassName?: string;
  inputClassName?: string;
  hasBorder?: boolean;
  autoFocus?: boolean;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  useDarkStyle?: boolean;
};

export const SearchInput = (props: Props) => {
  const {
    disabled,
    value,
    onChange,
    placeholder,
    onSearch,
    className,
    iconClassName,
    inputClassName,
    onKeyDown,
    hasBorder = true,
    autoFocus,
    onFocus,
    onBlur,
    useDarkStyle,
  } = props;

  const classList = classnames('search-input', className, {
    'search-input--no-submit': !onSearch,
    'search-input--has-border': hasBorder,
    'search-input--disabled': disabled,
  });

  const iconClassList = classnames('search-input__icon-image', iconClassName, {
    'search-input__icon-image-active': !!value,
  });

  const inputClassList = classnames('search-input__input', inputClassName);

  const handleOnInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.value = trimStart(e.target.value);
  };

  const handleOnKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === ENTER_KEY_NAME) {
      const target = e.target as HTMLInputElement;

      if (onSearch) {
        onSearch(target.value);
      }

      if (onKeyDown) {
        onKeyDown(e);
      }

      e.preventDefault();
    }
  };

  const onIconClick = () => {
    if (onSearch) {
      onSearch(value ?? '');
    }
  };

  return (
    <div className={classList}>
      <img
        src={useDarkStyle ? searchDarkIconBeta : searchLightIconBeta}
        className={iconClassList}
        alt="Search Icon"
        onClick={onIconClick}
      />

      <input
        disabled={disabled}
        type="search"
        className={inputClassList}
        placeholder={placeholder}
        value={value ?? ''}
        onInput={handleOnInput}
        onChange={onChange}
        onKeyDown={handleOnKeyDown}
        autoFocus={autoFocus}
        onFocus={onFocus}
        onBlur={onBlur}
      />
    </div>
  );
};
